import { format } from "date-fns";

import { CircleClose, Edit } from "@/components/icons";
import Badge from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import ScrollFadeWrapper from "@/components/ui/scroll-fade-wrapper";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { RecurringInvoice } from "@/features/invoices/types/recurring";

import RecurringIcon from "./RecurringIcon";

interface RecurringInvoiceTableProps {
  recurringInvoices: RecurringInvoice[];
  isLoading: boolean;
  onCancel?: (id: string) => void;
  onViewDetails: (id: string) => void;
  onEdit?: (id: string) => void;
  showCancelButton?: boolean;
}

const formatCurrency = (amount: number, currency: string) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(amount / 100);
};

const formatFrequency = (frequency: number) => {
  const unitLabel = frequency === 1 ? "month" : "months";
  return `Every ${frequency} ${unitLabel}`;
};

const formatDate = (timestamp: number) => {
  return format(new Date(timestamp * 1000), "MMM d, yyyy");
};

const RecurringInvoiceTable = ({
  recurringInvoices,
  isLoading,
  onCancel,
  onViewDetails,
  onEdit,
  showCancelButton = true,
}: RecurringInvoiceTableProps) => {
  const getStatusBadge = (status: string) => {
    switch (status) {
      // Ended/inactive statuses
      case "canceled":
        return (
          <Badge
            color="grey"
            variant="opaque"
            className="px-[6px] py-[2px] text-[12px] font-semibold"
          >
            Canceled
          </Badge>
        );
      case "completed":
        return (
          <Badge
            color="blue"
            variant="opaque"
            className="px-[6px] py-[2px] text-[12px] font-semibold"
          >
            Completed
          </Badge>
        );
      case "released":
        return (
          <Badge
            color="grey"
            variant="opaque"
            className="px-[6px] py-[2px] text-[12px] font-semibold"
          >
            Released
          </Badge>
        );
      // Warning statuses
      case "paused":
        return (
          <Badge
            color="yellow"
            variant="opaque"
            className="px-[6px] py-[2px] text-[12px] font-semibold"
          >
            Paused
          </Badge>
        );
      case "past_due":
        return (
          <Badge
            color="yellow"
            variant="opaque"
            className="px-[6px] py-[2px] text-[12px] font-semibold"
          >
            Past Due
          </Badge>
        );
      case "incomplete":
        return (
          <Badge
            color="yellow"
            variant="opaque"
            className="px-[6px] py-[2px] text-[12px] font-semibold"
          >
            Incomplete
          </Badge>
        );
      case "unpaid":
        return (
          <Badge
            color="red"
            variant="opaque"
            className="px-[6px] py-[2px] text-[12px] font-semibold"
          >
            Unpaid
          </Badge>
        );
      // Pending statuses
      case "trialing":
        return (
          <Badge
            color="blue"
            variant="opaque"
            className="px-[6px] py-[2px] text-[12px] font-semibold"
          >
            Trialing
          </Badge>
        );
      case "not_started":
        return (
          <Badge
            color="grey"
            variant="opaque"
            className="px-[6px] py-[2px] text-[12px] font-semibold"
          >
            Not Started
          </Badge>
        );
      // Active status
      case "active":
      default:
        return (
          <Badge
            color="green"
            variant="opaque"
            className="px-[6px] py-[2px] text-[12px] font-semibold"
          >
            Active
          </Badge>
        );
    }
  };

  const getNextInvoiceText = (invoice: RecurringInvoice) => {
    if (invoice.status === "canceled" && invoice.canceledAt) {
      return formatDate(invoice.canceledAt);
    }
    if (invoice.status === "completed" && invoice.completedAt) {
      return formatDate(invoice.completedAt);
    }
    if (!invoice.nextInvoiceAt) {
      return "—";
    }
    return formatDate(invoice.nextInvoiceAt);
  };

  return (
    <ScrollFadeWrapper>
      <Table
        bordered={false}
        className="w-full min-w-[800px] border-collapse text-[12px] leading-[16px]"
      >
        <TableHeader className="bg-transparent">
          <TableRow>
            <TableHead className="px-3 py-1.5 text-[10px] font-semibold tracking-[-0.4px] text-[#767677] text-left whitespace-nowrap">
              Customer
            </TableHead>
            <TableHead className="px-3 py-1.5 text-[10px] font-semibold tracking-[-0.4px] text-[#767677] text-left whitespace-nowrap">
              Amount
            </TableHead>
            <TableHead className="px-3 py-1.5 text-[10px] font-semibold tracking-[-0.4px] text-[#767677] text-left whitespace-nowrap min-w-[140px]">
              Frequency
            </TableHead>
            <TableHead className="px-3 py-1.5 text-[10px] font-semibold tracking-[-0.4px] text-[#767677] text-left whitespace-nowrap">
              {showCancelButton ? "Next Invoice" : "Ended"}
            </TableHead>
            <TableHead className="px-3 py-1.5 text-[10px] font-semibold tracking-[-0.4px] text-[#767677] text-left whitespace-nowrap">
              Invoices Sent
            </TableHead>
            <TableHead className="px-3 py-1.5 text-[10px] font-semibold tracking-[-0.4px] text-[#767677] text-left whitespace-nowrap">
              Status
            </TableHead>
            <TableHead className="px-3 py-1.5 text-[10px] font-semibold tracking-[-0.4px] text-[#767677] text-right pr-6 whitespace-nowrap">
              {showCancelButton ? "Actions" : ""}
            </TableHead>
          </TableRow>
        </TableHeader>
      <TableBody className="text-[12px] leading-[16px]">
        {isLoading ? (
          [...Array(4)].map((_, index) => (
            <TableRow
              key={`skeleton-${index}`}
              className="border-b border-[#F1F1F1] last:border-none h-11"
            >
              <TableCell className="px-3 py-1.5">
                <Skeleton className="h-4 w-3/4" />
              </TableCell>
              <TableCell className="px-3 py-1.5">
                <Skeleton className="h-4 w-2/3" />
              </TableCell>
              <TableCell className="px-3 py-1.5">
                <Skeleton className="h-4 w-2/3" />
              </TableCell>
              <TableCell className="px-3 py-1.5">
                <Skeleton className="h-4 w-2/3" />
              </TableCell>
              <TableCell className="px-3 py-1.5">
                <Skeleton className="h-4 w-1/3" />
              </TableCell>
              <TableCell className="px-3 py-1.5">
                <Skeleton className="h-6 w-16 rounded-full" />
              </TableCell>
              <TableCell className="px-3 py-1.5 text-right pr-6">
                <div className="flex items-center justify-end h-8">
                  {showCancelButton && (
                    <Skeleton className="h-8 w-8 rounded-full" />
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))
        ) : recurringInvoices.length === 0 ? (
          <TableRow className="border-none">
            <TableCell colSpan={7} className="h-32 text-left">
              <div className="text-[#767677] text-sm font-semibold">
                {showCancelButton
                  ? "No active recurring invoices"
                  : "No ended recurring invoices"}
              </div>
            </TableCell>
          </TableRow>
        ) : (
          recurringInvoices.map((invoice) => (
            <TableRow
              key={invoice.id}
              onClick={(e) => {
                if ((e.target as HTMLElement).closest("button") === null) {
                  onViewDetails(invoice.id);
                }
              }}
              className="cursor-pointer hover:bg-[#fafafa] transition-colors border-b border-[#F1F1F1] last:border-none group h-11"
            >
              <TableCell className="px-3 py-1.5 text-[12px] tracking-tight text-[#494949] font-semibold overflow-hidden">
                <div className="flex items-center gap-2 overflow-hidden">
                  <span className="truncate">{invoice.customerName}</span>
                  <div className="flex-shrink-0">
                    <RecurringIcon
                      recurringInvoiceId={invoice.id}
                      customerName={invoice.customerName}
                      onClick={onViewDetails}
                    />
                  </div>
                </div>
              </TableCell>
              <TableCell className="px-3 py-1.5 text-[12px] tracking-tight text-[#494949] font-semibold truncate">
                {formatCurrency(invoice.amount, invoice.currency)}
              </TableCell>
              <TableCell className="px-3 py-1.5 text-[12px] tracking-tight text-[#494949] font-semibold truncate">
                {formatFrequency(invoice.frequency)}
              </TableCell>
              <TableCell className="px-3 py-1.5 text-[12px] tracking-tight text-[#494949] font-semibold truncate">
                {getNextInvoiceText(invoice)}
              </TableCell>
              <TableCell className="px-3 py-1.5 text-[12px] tracking-tight text-[#494949] font-semibold truncate">
                {invoice.invoicesSent}
              </TableCell>
              <TableCell className="px-3 py-1.5 text-[12px] tracking-tight font-semibold">
                {getStatusBadge(invoice.status)}
              </TableCell>
              <TableCell className="px-3 py-1.5 text-right pr-6">
                <div className="flex items-center justify-end gap-2 h-8">
                  {showCancelButton && invoice.status === "active" && onEdit && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onEdit(invoice.id);
                      }}
                      className="h-8 w-8 p-0 text-[#767677] hover:text-[#494949] hover:bg-[#f5f5f5]"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                  )}
                  {showCancelButton && invoice.status === "active" && onCancel && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onCancel(invoice.id);
                      }}
                      className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <CircleClose className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
    </ScrollFadeWrapper>
  );
};

export default RecurringInvoiceTable;
